import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DocusignService } from 'src/app/services/docusign.service';
import { UserService } from 'src/app/services/user.service';``
import * as FileSaver from 'file-saver';
import { environment } from '../../../../environments/environment';
import { productCategory } from 'src/config/metadata-config';

@Component({
  selector: 'upsc-docusign',
  templateUrl: './docusign.component.html',
  styleUrls: ['./docusign.component.scss'],
})

export class DocusignComponent implements OnInit {
  @Output() isDocusignComplete = new EventEmitter<boolean>();
  @Output() docusignFailed = new EventEmitter<boolean>();

  //Form
  docusignForm: FormGroup;

  //Display booleans
  isUser: boolean = true;
  isDocuSigned: boolean = false;
  
  //Data holders
  contactName: string;
  emailAddress: string;
  claimInfo;
  baseURL = environment.baseUrl.web;

  constructor(
    private docusignService: DocusignService,
    private userService: UserService,
    private fb: FormBuilder
  ) {
    this.contactName = this.userService.getUserInfo().contactName;
    this.emailAddress = this.userService.getUserInfo().emailAddress;
    this.claimInfo = JSON.parse(sessionStorage.getItem('claimModel'));

    this.docusignForm = this.fb.group({
      name: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]]
    });
  }

  ngOnInit(): void {
    this.isDocusignComplete.emit(false);
  }

  onSubmit() {
    if (!this.isUser && this.docusignForm.invalid) {
      this.name.markAsTouched();
      this.email.markAsTouched();
      return;
    }
    
    let request = {
      signerName: this.name.value ? this.name.value : this.createSignerName(),
      signerEmail: this.email.value ? this.email.value : this.createSignerEmail(),
      returnURL: `${this.baseURL}/docusign-confirmation`,
      subrogor: this.claimInfo.persistPayeeName ? this.claimInfo.persistPayeeName : this.contactName,
      // bordereauxNumber: '',
      // claimNumber: '',
      carrier: this.createCarrierString(),
      billsOfLading: this.claimInfo?.trackingNumber_Ext,
      origin: this.createOriginString(),
      destination: this.createDestinationString(),
      cargoDescription: this.createProductCategoryString(),
      lossNature: this.createLossNatureString(),
      lossDate: this.claimInfo?.lossDate?.toString(),
      // claimedValue: this.claimInfo?.claimAmount_Ext
    }
    this.docusignService.postEnvelopeData(request).subscribe(
      response => {
        if (response && response?.data && response?.data?.envelopeId && response?.data?.redirectUrl) {
          //Store the envelopeId
          this.createSigningTab(response.data.envelopeId, response.data.redirectUrl);
          this.checkDocusignTab();
        } else {
          //error, allow the user to continue to submitClaim
          this.docusignFailed.emit(true);
        }
      }, error => {
        this.docusignFailed.emit(true);
      }
    );
  }

  toggleForm() {
    this.isUser = !this.isUser
  }

  /**
   * Save and initiate PDF download on browser
   */
  saveBase64ToPDF(fileContents: any, fileName: string): void{
    const byteCharacters = atob(fileContents);
    const byteArray = new Uint8Array([...byteCharacters].map(c => c.charCodeAt(0)));
    let file = new Blob([byteArray], {type: 'application/pdf'});
    FileSaver.saveAs(file, fileName);
  }

  createSigningTab(envelopeId, redirectUrl) {
    const win = window.open("", "_blank");
    if (win) {
      sessionStorage.setItem('envelopeId', envelopeId);
      win.location.href = redirectUrl;
    }
  }
  
  handleMessage = (event: MessageEvent) => {
    // Check the origin for security DO THIS IN PRODUCTION
    if (!this.baseURL.startsWith(event.origin)){
      return;
    }
    if (event.data?.type === 'signing-complete') {
      const envelopeId = sessionStorage.getItem("envelopeId");
      this.docusignService.checkEnvelopeStatus(envelopeId, this.email.value ? this.email.value : this.emailAddress, this.claimInfo?.trackingNumber_Ext).subscribe(
        response => {
          // State: Loading, Complete 
          this.isDocuSigned = true;
          this.isDocusignComplete.emit(true);
          this.cleanupDocusignListener();
        }, error => {
          this.isDocuSigned = false;
          this.cleanupDocusignListener();
        }
      );
    }
  };

  checkDocusignTab() {
    window.addEventListener('message', this.handleMessage);
  }

  cleanupDocusignListener() {
    window.removeEventListener('message', this.handleMessage);
  }

  createDestinationString() {
    let destination = `${this.claimInfo?.shippingDetailsDTO?.receiverDetails?.city}/${this.claimInfo?.shippingDetailsDTO?.receiverDetails?.state}`;
    return destination;
  }

  createOriginString() {
    let origin = `${this.claimInfo?.shippingDetailsDTO?.senderDetails?.city}/${this.claimInfo?.shippingDetailsDTO?.senderDetails?.state}`;
    return origin;
  }

  createSignerName() {
    let name = `${this.claimInfo?.mainContact?.firstName} ${this.claimInfo?.mainContact?.lastName}`;
    if (!name) {
      return this.contactName;
    } else {
      return name;
    }
  }

  createSignerEmail() {
    let email = `${this.claimInfo?.mainContact?.emailAddress1}`;
    if (!email) {
      return this.emailAddress;
    } else {
      return email;
    }
  }

  createLossNatureString() {
    let lossCause = this.claimInfo?.lossCause;
    if (lossCause == 'loss_Ext') {
      return 'Loss';
    } else if (lossCause == 'damage_Ext') {
      return 'Damage';
    } else if (lossCause == 'fallout_Ext') {
      return 'Missing';
    } else if (lossCause == 'delay') {
      return 'Late Delivery';
    } else {
      return '';
    }
  }

  createCarrierString() {
    let carrier = this.claimInfo?.transportationCarrierId_Ext;
    let otherCarrier = this.claimInfo?.otherCarrier;
    if (otherCarrier) {
      return otherCarrier;
    } else {
      return carrier;
    }
  }

  createProductCategoryString() {
    let product = this.claimInfo?.productCategory_Ext;

    let categoryList = Object.entries(productCategory).map(([key, value]) => ({ key, value }));

    let translatedProduct = categoryList.find(x => x.key === product)?.value || '';
    return translatedProduct;
  }

  get name() { return this.docusignForm.controls.name; }
  get email() { return this.docusignForm.controls.email; }
}

